import React, { useMemo } from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    HomePageCarouselSingleItem,
    CardFooterText,
    CardFooterWrapper,
    CardBodyActionWrapper,
    CardButtonWrapper,
    TcApplied,
    CardButton,
    LazyImageItem,
    OptedInBadgeWrapper,
    CheckIcon,
    OptInButtonComponentWrapper,
} from './HomePageCarouselPromos.style';
import { HomePageCarouselItemState } from './HomePageCarouselItem.state';
import { CardBodyComponent } from './CardBodyComponent';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import PlaceholderImage from 'src/domains/layouts/webview/components/homePageCarouselPromos/assets/default_hpc_image.webp';
import { useCommon } from 'src/domains/common/Common';
import {
    ActiveHomePageCarouselType,
    isBonusEnginePromo,
    isOptInPromo,
} from 'src/domains/layouts/state/cmsState/homePageCarouselState/HomePageCarouselState';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { EighteenPlusCircleFilledIcon } from 'src/domains/layouts/shared/assets/icons/EighteenPlusCircleFilledIcon';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { AppCasinoState } from 'src/domains/casino/state/AppCasino.state';

interface ItemTypeBonusEnginePropsType {
    promo: ActiveHomePageCarouselType;
}

type ButtonComponentPropsType = {
    btn_title: string;
    btnColor?: string;
    disabled?: boolean;
    isOptedIn?: boolean | null;
    onClick?: () => void;
};

const ButtonComponent = ({
    btn_title,
    btnColor,
    disabled = false,
    isOptedIn = false,
    onClick,
}: ButtonComponentPropsType): JSX.Element => {
    return (
        <CardButtonWrapper isOptedIn={isOptedIn}>
            <CardButton
                type='button'
                size='xs'
                btnColor={btnColor}
                dataTest='promo-carousel-button'
                onClick={onClick}
                disabled={disabled}
            >
                {btn_title}
            </CardButton>
        </CardButtonWrapper>
    );
};

const BetNowCTA = ({
    btn_title,
    btnColor,
    state,
    carouselType,
}: Omit<ButtonComponentPropsType, 'btn_title'> & { btn_title: ActiveHomePageCarouselType['btn_title'] } & {
    state: HomePageCarouselItemState;
    carouselType: string;
}): JSX.Element | null => {
    const { actionTemplate, handleMatchUrl, handleRedirect } = state;
    const defaultText = carouselType === 'casino' ? 'Play Here' : 'Bet Here';
    const btnTitle = typeof btn_title === 'string' && btn_title.length > 0 ? btn_title : defaultText;

    const onClick = actionTemplate === 'direct-link' ? handleMatchUrl : handleRedirect;

    return typeof actionTemplate === 'string' ? (
        <ButtonComponent
            isOptedIn={state.isOptedIn}
            btn_title={btnTitle}
            btnColor={btnColor}
            onClick={onClick}
        />
    ) : null;
};

const OptInBadge = () => {
    const common = useCommon();
    const {
        config: {
            componentVariants: { buttonVariant },
        },
    } = ConfigComponents.get(common);
    return (
        <OptedInBadgeWrapper variant={buttonVariant}>
            <CheckIcon />
            <span>Opted In</span>
        </OptedInBadgeWrapper>
    );
};

const OptInButtonComponent = observer(
    'OptInButtonComponent',
    ({ promo, state }: ItemTypeBonusEnginePropsType & { state: HomePageCarouselItemState }): JSX.Element | null => {
        const common = useCommon();
        const { btn_title, btn_color, carousel_type } = promo;
        const { handleOptIn, isOptedIn, handleRedirectToLogin } = state;
        const btnColor = typeof btn_color === 'string' && btn_color.length > 0 ? btn_color : undefined;
        const isNotOptedIn = isOptInPromo(promo) && (isOptedIn === false || isOptedIn === null);

        return isNotOptedIn ? (
            <ButtonComponent
                btn_title='Opt In'
                btnColor={btnColor}
                onClick={isOptedIn === false ? handleOptIn.bind(null, common) : handleRedirectToLogin}
            />
        ) : (
            <OptInButtonComponentWrapper>
                {isOptedIn === true ? <OptInBadge /> : null}
                <BetNowCTA
                    btn_title={btn_title}
                    btnColor={btnColor}
                    state={state}
                    carouselType={carousel_type}
                />
            </OptInButtonComponentWrapper>
        );
    }
);

export const ItemTypeBonusEngine = observer('ItemTypeBonusEngine', ({ promo }: ItemTypeBonusEnginePropsType) => {
    const common = useCommon();
    const starRouter = StarRouter.get(common);
    const appCasinoState = AppCasinoState.get(common);

    const state = useMemo(() => new HomePageCarouselItemState(promo, starRouter, appCasinoState), [promo]);
    const { img_url, bg_color, tc_text } = promo;
    const { isShowTC, onApplyTC, isTooLongTerms } = state;
    const tcText = tc_text ?? '';

    const imageUrl = img_url ?? null;

    return (
        <HomePageCarouselSingleItem
            bgColor={bg_color ?? undefined}
            data-test={`promo-carousel-bonus-engine-item-${promo.title}`}
        >
            {imageUrl === null ? (
                <LazyImageItem
                    imgBase={{ src: PlaceholderImage }}
                    imgFit='contain'
                    alt=''
                    placeholder={true}
                />
            ) : (
                <LazyImageItem
                    imgBase={{ src: imageUrl }}
                    imgFit='contain'
                    alt=''
                    placeholder={true}
                />
            )}
            <CardBodyActionWrapper>
                <CardBodyComponent promo={promo} />
                {isBonusEnginePromo(promo) ? (
                    <OptInButtonComponent
                        promo={promo}
                        state={state}
                    />
                ) : null}
            </CardBodyActionWrapper>

            {tcText === '' ? null : (
                <CardFooterWrapper>
                    <EighteenPlusCircleFilledIcon />
                    {isShowTC === true || isTooLongTerms === false ? (
                        <CardFooterText
                            dangerouslySetInnerHTML={{ __html: tcText }}
                            data-test='promo-carousel-tc'
                        />
                    ) : (
                        <TcApplied
                            onClick={onApplyTC}
                            data-test='promo-carousel-tc'
                        >
                            <I18n
                                langKey='promos.terms-and-conditions-apply'
                                defaultText='Terms and Conditions Apply'
                            />
                        </TcApplied>
                    )}
                </CardFooterWrapper>
            )}
        </HomePageCarouselSingleItem>
    );
});
